import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet, useNavigate, Navigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Package, 
  ShoppingCart, 
  Users, 
  MessageSquare,
  Menu,
  X,
  LogOut,
  ChefHat,
  UserCog,
  Shield,
  Tags,
  FileText,
  FileSignature,
  BellRing,
  BarChart3,
  Clock,
  Globe,
  AlertTriangle,
  CalendarDays,
  CalendarCheck,
  ChefHat as ChefHatIcon,
  Boxes,
  PackageSearch,
  HelpCircle,
  TrendingUp,
  Repeat,
  Inbox,
  Gauge
} from 'lucide-react';
import { Button } from '../components/ui/button';
import NotificationBell from '../components/NotificationBell';

const navItems = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/admin/products', label: 'Products', icon: Package },
  { path: '/admin/orders', label: 'Orders', icon: ShoppingCart },
  { path: '/admin/recurring-orders', label: 'Recurring Orders', icon: Repeat },
  { path: '/admin/menu-calendar', label: 'Menu Calendar', icon: CalendarDays },
  { path: '/admin/scheduling', label: 'Coverage', icon: CalendarCheck, adminOnly: true },
  { path: '/admin/scheduling/builder', label: 'Schedule Builder', icon: Clock, adminOnly: true },
  { path: '/admin/scheduling/kitchens', label: 'Kitchen Setup', icon: ChefHatIcon, adminOnly: true },
  { path: '/admin/scheduling/requests', label: 'Change Requests', icon: Inbox, adminOnly: true },
  { path: '/admin/inventory', label: 'Inventory', icon: Boxes },
  { path: '/admin/invoices', label: 'Invoices', icon: FileText },
  { path: '/admin/clients', label: 'Clients', icon: Users },
  { path: '/admin/client-types', label: 'Pricing Tiers', icon: Tags },
  { path: '/admin/users', label: 'Users', icon: UserCog },
  { path: '/admin/messages', label: 'Messages', icon: MessageSquare },
  { path: '/admin/documents', label: 'Documents', icon: FileSignature },
  { path: '/admin/help', label: 'Help Center', icon: HelpCircle },
  { path: '/admin/notification-settings', label: 'Notifications', icon: BellRing },
  { path: '/admin/reports', label: 'Reports', icon: BarChart3 },
  { path: '/admin/order-insights', label: 'Order Insights', icon: TrendingUp },
  { path: '/admin/over-ordering', label: 'Over-ordering', icon: Gauge },
  { path: '/admin/settings/ordering-deadlines', label: 'Ordering Deadlines', icon: Clock },
  { path: '/admin/settings/timezone', label: 'Timezone', icon: Globe },
  { path: '/admin/settings/overdue-reminders', label: 'Overdue Reminders', icon: AlertTriangle },
  { path: '/admin/settings/inventory-notifications', label: 'Inventory Alerts', icon: PackageSearch },
  { path: '/admin/settings/scheduling', label: 'Scheduling Settings', icon: CalendarCheck, adminOnly: true },
  { path: '/admin/security', label: 'Security', icon: Shield },
];

// Kitchen bookings cost money and the rota is an employment record, so the
// scheduling surfaces are strict-admin on the server. Match that here rather
// than painting the shell and letting every fetch 403.
const STRICT_ADMIN_PREFIXES = ['/admin/scheduling', '/admin/settings/scheduling'];

const isStrictAdminPath = (pathname) =>
  STRICT_ADMIN_PREFIXES.some((prefix) => pathname.startsWith(prefix));

const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('unauthenticated');
      const data = await res.json();
      if (!['admin', 'bakery_staff'].includes(data.role)) {
        navigate('/admin/login');
        return;
      }
      localStorage.setItem('admin_user', JSON.stringify(data));
      setUser(data);
      setIsReady(true);
    } catch {
      localStorage.removeItem('admin_user');
      navigate('/admin/login');
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // ignore network errors on logout
    }
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  const isActive = (path) => location.pathname === path;

  const visibleNavItems = navItems.filter(
    (item) => !item.adminOnly || user?.role === 'admin'
  );

  // Keep a non-admin out of the strict-admin surfaces even by direct URL.
  if (isReady && user?.role !== 'admin' && isStrictAdminPath(location.pathname)) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  // Show loading while checking auth
  if (!isReady) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500"></div>
          <p className="mt-4 text-slate-400">Loading admin portal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-layout">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-slate-900 text-white flex flex-col transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="admin-sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-700 flex-shrink-0">
          <Link to="/admin/dashboard" className="flex items-center gap-2">
            <div className="p-2 bg-amber-500 rounded-lg">
              <ChefHat className="h-5 w-5 text-white" />
            </div>
            <div>
              <span className="font-bold text-lg">MAD Donuts</span>
              <p className="text-xs text-slate-400">Admin Portal</p>
            </div>
          </Link>
          <button 
            className="lg:hidden p-1 text-slate-400 hover:text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation — scrollable region */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1" data-testid="admin-nav">
          {visibleNavItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive(item.path)
                    ? 'bg-amber-500 text-white'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* User section pinned at bottom */}
        <div className="p-4 border-t border-slate-700 flex-shrink-0">
          {user && (
            <div className="mb-3 px-3">
              <p className="text-sm font-medium text-white truncate">{user.contact_name || user.email}</p>
              <p className="text-xs text-slate-400 capitalize">{user.role}</p>
            </div>
          )}
          <button 
            onClick={handleLogout}
            data-testid="logout-button"
            aria-label="Logout"
            className="flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-white w-full rounded-lg hover:bg-slate-800"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main content area */}
      <div className="lg:pl-64">
        {/* Top header */}
        <header className="sticky top-0 z-30 h-16 bg-white border-b flex items-center justify-between px-4 lg:px-6 shadow-sm">
          <button 
            className="lg:hidden p-2 -ml-2"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-6 w-6" />
          </button>

          <div className="flex-1 lg:flex-none">
            <h1 className="text-lg font-semibold text-slate-900 hidden lg:block">
              {navItems.find(item => isActive(item.path))?.label || 'Admin'}
            </h1>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-4">
            <NotificationBell role="admin" />
            <Link to="/home">
              <Button variant="outline" size="sm">
                View Public Site
              </Button>
            </Link>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6" data-testid="admin-main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
